import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

export type RockMassifStyle = "peak" | "plateau" | "wall";

const PALETTE = [0xb4b2aa, 0xa3a19a, 0xc2c0b8, 0x96948d, 0xadaaa1].map((color) => new THREE.Color(color));
const MOSS = new THREE.Color(0x7d9157);
/** Au-delà, les colonnes s'élargissent : un très grand massif reste un seul maillage raisonnable. */
const MAX_COLUMNS = 700;

function seededRandom(seed: number): () => number {
  let state = Math.abs(Math.floor(seed)) % 2147483646 + 1;
  return () => (state = (state * 16807) % 2147483647) / 2147483647;
}

/** Hauteur relative d'une colonne selon sa position dans l'emprise, de 0 à 1 (bord à centre). */
function relativeHeight(style: RockMassifStyle, u: number, v: number, spires: [number, number][]): number {
  const d = Math.hypot(u, v);
  if (style === "wall") {
    const ends = Math.min(1, (1 - Math.abs(v)) * 4);
    return 0.65 + 0.35 * ends - 0.15 * Math.abs(u);
  }
  if (style === "plateau") return 0.78 + 0.22 * Math.pow(Math.max(0, 1 - d), 0.15);
  const dome = Math.pow(Math.max(0, 1 - d), 0.9);
  const spire = Math.max(...spires.map(([x, z]) => Math.exp(-((u - x) ** 2 + (v - z) ** 2) * 10)));
  return Math.min(1, 0.1 + 0.42 * dome + 0.5 * spire);
}

/**
 * Massif rocheux en colonnes verticales serrées, à la manière de menhirs : `width` le long de x,
 * `length` le long de z, `height` au plus haut. La forme ne dépend que de ses dimensions et de son
 * style : deux massifs identiques partagent leur gabarit.
 */
export function buildRockMassif(width: number, length: number, height: number, style: RockMassifStyle): THREE.Group {
  const random = seededRandom(width * 7919 + length * 104729 + height * 1299709 + style.length);
  const rx = width / 2;
  const rz = length / 2;
  const spacing = Math.max(0.8, Math.min(width, length) / 14, Math.sqrt((width * length) / MAX_COLUMNS));
  const spires: [number, number][] = [[0, 0], [(random() - 0.5) * 0.6, (random() - 0.5) * 0.6], [(random() - 0.5) * 0.6, (random() - 0.5) * 0.6]];
  const geometries: THREE.BufferGeometry[] = [];

  const column = (x: number, z: number, radius: number, columnHeight: number, color: THREE.Color, mossy: boolean) => {
    const sides = 5 + Math.floor(random() * 3);
    const geometry = new THREE.CylinderGeometry(radius * (0.75 + random() * 0.15), radius, columnHeight, sides, 1);
    geometry.translate(0, columnHeight / 2, 0);
    const position = geometry.attributes.position!;
    const tiltX = (random() - 0.5) * 0.35;
    const tiltZ = (random() - 0.5) * 0.35;
    for (let i = 0; i < position.count; i++) {
      if (position.getY(i) > columnHeight - 1e-3) position.setY(i, columnHeight + position.getX(i) * tiltX + position.getZ(i) * tiltZ);
    }
    geometry.rotateY(random() * Math.PI);
    geometry.translate(x, 0, z);
    const flat = geometry.toNonIndexed();
    flat.computeVertexNormals();
    const normal = flat.attributes.normal!;
    const colors = new Float32Array(flat.attributes.position!.count * 3);
    const face = new THREE.Color();
    for (let i = 0; i < flat.attributes.position!.count; i += 3) {
      const top = (normal.getY(i) + normal.getY(i + 1) + normal.getY(i + 2)) / 3 > 0.6;
      face.copy(top && mossy ? MOSS : color).multiplyScalar(top && mossy ? 1 : 0.93 + random() * 0.1);
      for (let k = 0; k < 3; k++) face.toArray(colors, (i + k) * 3);
    }
    flat.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    geometries.push(flat);
  };

  let row = 0;
  for (let z = -rz; z <= rz; z += spacing * 0.87, row++) {
    for (let x = -rx + (row % 2) * (spacing / 2); x <= rx; x += spacing) {
      const jx = x + (random() - 0.5) * spacing * 0.6;
      const jz = z + (random() - 0.5) * spacing * 0.6;
      const u = jx / rx;
      const v = jz / rz;
      if (style !== "wall" && Math.hypot(u, v) > 1) continue;
      if (style === "wall" && (Math.abs(u) > 1 || Math.abs(v) > 1)) continue;
      const relative = relativeHeight(style, u, v, spires) * (0.85 + random() * 0.25);
      const columnHeight = Math.min(height, Math.max(spacing, height * relative));
      column(jx, jz, spacing * (0.62 + random() * 0.2), columnHeight, PALETTE[Math.floor(random() * PALETTE.length)]!, random() < 0.45);
    }
  }

  const root = new THREE.Group();
  if (geometries.length) {
    const mesh = new THREE.Mesh(
      mergeGeometries(geometries),
      new THREE.MeshStandardMaterial({ name: "rock-massif", vertexColors: true, roughness: 0.95, metalness: 0, flatShading: true }),
    );
    mesh.name = "RockMassif";
    root.add(mesh);
  }
  return root;
}
